import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import FinalCta from "../sections/FinalCta";
import { IMG, resolveImage } from "../data/content";

const BLOCKS = [
  {
    title: "Walk the Forest",
    image: resolveImage("walk", 1000, 1200),
    text: "The Jamoon sits deep in the forest on the banks of the Ramganga, and trails run out from it in every direction. Explore them on foot with our naturalists — walking safaris reveal what no vehicle ever will.",
  },
  {
    title: "Camera Traps",
    image: resolveImage("tigerFeeding", 1000, 1200),
    text: "The lodge keeps its own trail cameras, which guests can deploy to capture elusive cats and reptiles. Bring your own cameras and set them within the lodge grounds, if you wish.",
  },
  {
    title: "The Photo Hide",
    image: resolveImage("elephantsBank", 1000, 1200),
    text: "Our hide overlooks a private waterhole frequented by creatures of day and night. From it, guests photograph big cats, elephants, birds and reptiles without disturbing them.",
  },
  {
    title: "Birding",
    image: resolveImage("barbets", 1000, 1200),
    text: "More than 300 species of birds live in and around the lodge — some are permanent residents. Use the bird hide within the property, or walk out on a birding hike at first light.",
  },
  {
    title: "Night & Astro",
    image: resolveImage("dusk", 1000, 1200),
    text: "With no electricity in or around the lodge, the sky is lit with stars every night. Practise astro photography under a genuinely dark sky — who knows, you might even discover a new star.",
  },
  {
    title: "Night Photography",
    image: resolveImage("mantis", 1000, 1200),
    text: "Try your hand at night photography — flashlight work, backlit macro subjects, the small world that wakes after dark. It is a totally different forest out there at night.",
  },
];

export default function ExperiencesPage() {
  return (
    <main data-testid="experiences-page">
      <Seo
        title="Wilderness Experiences | The Jamoon, Corbett"
        description="Explore wildlife, forest walks, birding, photography, camera traps, river experiences and nights under the stars at The Jamoon in Corbett."
        path="/experiences"
        image={IMG.river}
        imageAlt="Elephants on the Ramganga near The Jamoon in Corbett"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Experiences", path: "/experiences" }]}
      />
      <PageHero
        testid="experiences-hero"
        image={IMG.river}
        imageAlt="Elephants on the Ramganga near The Jamoon in Corbett"
        eyebrow="Experiences"
        title={<>Wilderness <span className="italic text-brass">Experiences</span></>}
        sub="Nothing is staged and nothing is scheduled. These are the ways the forest lets you in."
      />

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-12 sm:py-32 lg:px-20">
        {BLOCKS.map((b, i) => (
          <div
            key={b.title}
            data-testid={`experience-block-${i}`}
            className={`grid items-center gap-10 py-14 lg:grid-cols-2 lg:gap-20 ${i > 0 ? "border-t border-ivory/10" : ""}`}
          >
            <Reveal className={`group relative overflow-hidden ${i % 2 === 1 ? "lg:order-2" : ""}`}>
              <img
                src={b.image}
                alt={`${b.title} at The Jamoon, Corbett`}
                loading="lazy"
                className="aspect-[5/4] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/40 to-transparent" />
            </Reveal>
            <div className={i % 2 === 1 ? "lg:order-1" : ""}>
              <Reveal>
                <span className="font-serif text-lg italic text-brass/70">{String(i + 1).padStart(2, "0")}</span>
              </Reveal>
              <Reveal delay={0.08}>
                <h2 className="mt-3 font-serif text-3xl font-light leading-tight tracking-tight text-ivory sm:text-4xl">
                  {b.title}
                </h2>
              </Reveal>
              <Reveal delay={0.16}>
                <p className="mt-6 max-w-lg text-base font-light leading-relaxed text-ivory/70">{b.text}</p>
              </Reveal>
            </div>
          </div>
        ))}
      </section>

      <FinalCta />
    </main>
  );
}
